import { getApiEndpoints } from "../config/AuthConfigContext";
import { getFirebaseAuth } from "../firebase/firebaseService";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";
import { logDataToConsole, logError } from "../utils/customLogger";
import { notifyExtensionLogOut } from "./extensionMessaging";
import type {
  BulkUpdatePayload,
  BulkUpdateResponse,
  FacilityOption,
  FacilityType,
  HealthCareFacilityModel,
  InviteMemberPayload,
  InviteMemberResponse,
  MemberManagementError,
  MfaChallengeState,
  MfaStatus,
  ProviderWithFacilities,
  RbacRoleId,
  RbacTokenErrorType,
  RbacTokenResult,
  RemoveMemberResponse,
  ResetTwoFactorResponse,
  DeleteMemberResponse,
  StoredTokenData,
  TokenFacilityInfo,
  TokenPermissions,
  TokenResponse,
  TokenRoleInfo,
  TokenUserInfo,
  UpdateMemberRolePayload,
  UpdateMemberRoleResponse,
  AssignableRoleId,
} from "../types";
import { RbacRoleId as RbacRoleIdEnum } from "../types";

class MemberManagementTokenManager {
  private static instance: MemberManagementTokenManager;

  private static readonly AUTO_REFRESH_BUFFER_MS = 5 * 60 * 1000;

  private token: string | null = null;
  private expirationTime: number | null = null;
  private currentFacilityId: string | null = null;
  private userInfo: TokenUserInfo | null = null;
  private facilityInfo: TokenFacilityInfo | null = null;
  private roleInfo: TokenRoleInfo | null = null;
  private lastErrorType: RbacTokenErrorType | null = null;
  private lastErrorMessage: string | null = null;
  private recoveryFacilityIds: string[] = [];

  private fetchPromise: Promise<string | null> | null = null;
  private pendingFacilityId: string | null = null;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Subscribers for `useSyncExternalStore` when cached RBAC role changes. */
  private rbacRoleListeners = new Set<() => void>();

  // Pending second-factor (TOTP) challenge state. Set when a token exchange
  // returns `mfa_status`; cleared once a full token is issued.
  private mfaStatus: MfaStatus = null;
  private mfaToken: string | null = null;
  private mfaOtpauthUri: string | null = null;
  private mfaFacilityId: string | null = null;
  private mfaListeners = new Set<() => void>();
  private mfaSnapshot: MfaChallengeState = {
    status: null,
    mfaToken: null,
    otpauthUri: null,
  };

  private constructor() {}

  public static getInstance(): MemberManagementTokenManager {
    if (!MemberManagementTokenManager.instance) {
      MemberManagementTokenManager.instance =
        new MemberManagementTokenManager();
    }
    return MemberManagementTokenManager.instance;
  }

  private async getFirebaseIdToken(): Promise<string | null> {
    const currentUser = getFirebaseAuth().currentUser;
    if (!currentUser) {
      logError(new Error("No authenticated user found"), "getFirebaseIdToken");
      return null;
    }
    try {
      const idToken = await currentUser.getIdToken();
      return idToken;
    } catch (error) {
      logError(error as Error, "Error getting Firebase ID token");
      return null;
    }
  }

  /**
   * Parse token exchange failures and retain facility recovery metadata.
   *
   * @param status - HTTP response status
   * @param errorText - Serialized error response body
   * @returns Normalized RBAC error and allowed recovery facility IDs
   */
  private parseErrorResponse(
    status: number,
    errorText: string,
  ): {
    errorType: RbacTokenErrorType;
    errorMessage: string;
    allowedFacilityIds: string[];
  } {
    const { message, allowedFacilityIds } = this.extractErrorDetails(errorText);
    const errorMessage = message;
    const errorType = this.getErrorType(status, errorMessage);
    return { errorType, errorMessage, allowedFacilityIds };
  }

  /**
   * Extract a readable message and optional allowed facilities from an error.
   *
   * @param errorText - Serialized FastAPI error response
   * @returns Error message and facility IDs available to the current user
   */
  private extractErrorDetails(errorText: string): {
    message: string;
    allowedFacilityIds: string[];
  } {
    try {
      const { detail, message } = JSON.parse(errorText);
      if (detail && typeof detail === "object") {
        return {
          message: detail.message ?? message ?? errorText,
          allowedFacilityIds: Array.isArray(detail.allowed_facility_ids)
            ? detail.allowed_facility_ids
            : [],
        };
      }
      return {
        message: detail ?? message ?? errorText,
        allowedFacilityIds: [],
      };
    } catch {
      return {
        message: errorText || "An error occurred. Please try again.",
        allowedFacilityIds: [],
      };
    }
  }

  private getErrorType(status: number, message: string): RbacTokenErrorType {
    if (status === 401) return "unauthorized";

    if (status === 403) {
      const lowerMessage = message.toLowerCase();
      const isInactiveAccount =
        lowerMessage.includes("inactive") || lowerMessage.includes("disabled");
      return isInactiveAccount ? "inactive_account" : "forbidden";
    }

    return "unknown";
  }

  private isTokenValid(facilityId: string): boolean {
    return !!(
      this.token &&
      this.expirationTime &&
      this.currentFacilityId === facilityId &&
      Date.now() < this.expirationTime
    );
  }

  private scheduleTokenRefresh(facilityId: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!this.expirationTime) return;

    const refreshIn =
      this.expirationTime -
      Date.now() -
      MemberManagementTokenManager.AUTO_REFRESH_BUFFER_MS;

    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(() => {
        logDataToConsole("Auto-refreshing RBAC token before expiration");
        this.token = null;
        this.fetchToken(facilityId).catch((error) => {
          logError(error as Error, "Error during automatic token refresh");
        });
      }, refreshIn);
    }
  }

  public async fetchToken(facilityId: string): Promise<string | null> {
    this.lastErrorType = null;
    this.lastErrorMessage = null;

    if (this.isTokenValid(facilityId)) {
      return this.token;
    }

    if (this.fetchPromise && this.pendingFacilityId === facilityId) {
      return this.fetchPromise;
    }

    this.pendingFacilityId = facilityId;
    this.fetchPromise = this.performTokenFetch(facilityId);

    try {
      const result = await this.fetchPromise;
      return result;
    } finally {
      this.fetchPromise = null;
      this.pendingFacilityId = null;
    }
  }

  private async performTokenFetch(facilityId: string): Promise<string | null> {
    const firebaseToken = await this.getFirebaseIdToken();
    if (!firebaseToken) {
      console.error("Failed to get Firebase ID token");
      this.lastErrorType = "unauthorized";
      this.lastErrorMessage = "Failed to authenticate. Please log in again.";
      return null;
    }

    try {
      const endpoints = getApiEndpoints();
      const response = await fetch(endpoints.AUTH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "firebase-token": firebaseToken,
          "X-Facility-ID": facilityId,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Token exchange failed:", {
          status: response.status,
          statusText: response.statusText,
          facilityId: facilityId,
        });
        logError(
          new Error(
            `Token exchange failed: ${response.status} ${response.statusText}`,
          ),
          errorText,
        );

        const { errorType, errorMessage, allowedFacilityIds } =
          this.parseErrorResponse(response.status, errorText);
        this.lastErrorType = errorType;
        this.lastErrorMessage = errorMessage;
        this.recoveryFacilityIds = allowedFacilityIds;

        return null;
      }

      const data: TokenResponse = await response.json();

      // Second factor not yet satisfied: surface the challenge so the UI can
      // route to the enroll / verify screen. No token is cached here.
      if (data.mfa_status) {
        this.setMfaChallenge(
          data.mfa_status,
          data.mfa_token ?? null,
          data.otpauth_uri ?? null,
          facilityId,
        );
        return null;
      }
      this.clearMfaChallenge();

      logDataToConsole("Token exchange successful", {
        userName: data.user
          ? `${data.user.FirstName} ${data.user.LastName}`
          : undefined,
        facilityName: data.facility?.name,
        roleName: data.role?.name,
        expiresIn: data.expires_in,
      });

      return this.applyTokenData(data, facilityId);
    } catch (error) {
      logError(error as Error, "Error exchanging token");
      this.lastErrorType = "unknown";
      this.lastErrorMessage = "An error occurred. Please try again.";
      return null;
    }
  }

  /**
   * Cache a successful token-exchange response and schedule its refresh.
   *
   * When the access token value changes (rotation or facility switch), notifies
   * the paired browser extension to log out so its cached session stays in sync.
   *
   * @param data - Full token response from /auth/token or /auth/verify-totp
   * @param facilityId - Facility the token was minted for
   * @returns The cached access token
   */
  private applyTokenData(data: TokenResponse, facilityId: string): string {
    const previousToken = this.token;
    this.token = data.access_token;
    this.expirationTime = Date.now() + data.expires_in * 1000;
    this.currentFacilityId = facilityId;
    this.userInfo = data.user;
    this.facilityInfo = data.facility;
    this.roleInfo = data.role;
    this.recoveryFacilityIds = [];
    this.notifyRbacRoleListeners();
    this.scheduleTokenRefresh(facilityId);

    // A rotation/facility-switch invalidates any session the extension paired
    // against the prior token, so ask it to log out. Skip the initial login
    // (no prior token) and no-op re-applications of the same token value.
    if (previousToken && previousToken !== this.token) {
      notifyExtensionLogOut();
    }

    return this.token;
  }

  /**
   * Submit a TOTP code (with the pending challenge token) to complete the
   * second factor. On success the returned full token is cached exactly like a
   * normal exchange and the MFA challenge state is cleared.
   *
   * @param code - 6-digit code from the user's authenticator app
   * @returns RBAC token result describing success or the failure reason
   */
  public async verifyTotp(code: string): Promise<RbacTokenResult> {
    if (!this.mfaToken) {
      return {
        success: false,
        errorType: "unauthorized",
        errorMessage: "No MFA challenge in progress.",
      };
    }
    try {
      const endpoints = getApiEndpoints();
      const response = await fetch(endpoints.AUTH_VERIFY_TOTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_token: this.mfaToken, code }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const { errorType, errorMessage } = this.parseErrorResponse(
          response.status,
          errorText,
        );
        this.lastErrorType = errorType;
        this.lastErrorMessage = errorMessage;
        return { success: false, errorType, errorMessage };
      }

      const data: TokenResponse = await response.json();
      const facilityId = this.mfaFacilityId ?? this.currentFacilityId ?? "";
      this.clearMfaChallenge();
      this.applyTokenData(data, facilityId);
      return { success: true };
    } catch (error) {
      logError(error as Error, "Error verifying TOTP code");
      this.lastErrorType = "unknown";
      this.lastErrorMessage = "An error occurred. Please try again.";
      return {
        success: false,
        errorType: "unknown",
        errorMessage: this.lastErrorMessage,
      };
    }
  }

  private setMfaChallenge(
    status: MfaStatus,
    mfaToken: string | null,
    otpauthUri: string | null,
    facilityId: string,
  ): void {
    this.mfaStatus = status;
    this.mfaToken = mfaToken;
    this.mfaOtpauthUri = otpauthUri;
    this.mfaFacilityId = facilityId;
    this.mfaSnapshot = { status, mfaToken, otpauthUri };
    this.notifyMfaListeners();
  }

  private clearMfaChallenge(): void {
    if (this.mfaStatus === null && this.mfaToken === null) {
      return;
    }
    this.mfaStatus = null;
    this.mfaToken = null;
    this.mfaOtpauthUri = null;
    this.mfaFacilityId = null;
    this.mfaSnapshot = { status: null, mfaToken: null, otpauthUri: null };
    this.notifyMfaListeners();
  }

  /**
   * Subscribe to MFA challenge state changes (for `useSyncExternalStore`).
   *
   * @param listener - Invoked when the MFA challenge state changes.
   * @returns Unsubscribe function.
   */
  public subscribeToMfaState(listener: () => void): () => void {
    this.mfaListeners.add(listener);
    return () => {
      this.mfaListeners.delete(listener);
    };
  }

  public getMfaStateSnapshot(): MfaChallengeState {
    return this.mfaSnapshot;
  }

  private notifyMfaListeners(): void {
    this.mfaListeners.forEach((cb) => {
      cb();
    });
  }

  public getLastErrorType(): RbacTokenErrorType | null {
    return this.lastErrorType;
  }

  public getLastErrorMessage(): string | null {
    return this.lastErrorMessage;
  }

  /**
   * Clears the in-memory RBAC token and all associated cached identity state.
   *
   * Also notifies the paired browser extension to log out when a token was
   * previously cached, keeping the extension session aligned with the dashboard.
   * Return facility IDs supplied for recovery from a failed token exchange.
   *
   * @returns Facility IDs the authenticated user may switch to
   */
  public getRecoveryFacilityIds(): string[] {
    return [...this.recoveryFacilityIds];
  }

  /**
   * Clear the in-memory RBAC token and all associated cached identity state.
   *
   * Also notifies the paired browser extension to log out when a token was
   * previously cached, keeping the extension session aligned with the dashboard.
   *
   * @returns Nothing
   */
  public clearToken(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const hadToken = this.token !== null;

    this.token = null;
    this.expirationTime = null;
    this.currentFacilityId = null;
    this.userInfo = null;
    this.facilityInfo = null;
    this.roleInfo = null;
    this.lastErrorType = null;
    this.lastErrorMessage = null;
    this.recoveryFacilityIds = [];
    this.fetchPromise = null;
    this.pendingFacilityId = null;
    this.clearMfaChallenge();
    this.notifyRbacRoleListeners();

    // Tear down the paired extension session alongside the dashboard session.
    if (hadToken) {
      notifyExtensionLogOut();
    }
  }

  /**
   * Subscribe to RBAC role snapshot updates (used with `useSyncExternalStore`).
   *
   * @param listener - Invoked when cached role id may have changed.
   * @returns Unsubscribe function.
   */
  public subscribeToRbacRole(listener: () => void): () => void {
    this.rbacRoleListeners.add(listener);
    return () => {
      this.rbacRoleListeners.delete(listener);
    };
  }

  /**
   * Snapshot string for external store subscribers (empty when no RBAC session).
   *
   * @returns Cached `role.id`, or `""`.
   */
  public getRbacRoleIdSnapshot(): string {
    return this.roleInfo?.id ?? "";
  }

  private notifyRbacRoleListeners(): void {
    this.rbacRoleListeners.forEach((cb) => {
      cb();
    });
  }

  public getUserInfo(): TokenUserInfo | null {
    return this.userInfo;
  }

  public getFacilityInfo(): TokenFacilityInfo | null {
    return this.facilityInfo;
  }

  public getRoleInfo(): TokenRoleInfo | null {
    return this.roleInfo;
  }

  public getPermissions(): TokenPermissions | null {
    return this.roleInfo?.permissions ?? null;
  }

  public hasPermission(permission: keyof TokenPermissions): boolean {
    return this.roleInfo?.permissions?.[permission] ?? false;
  }

  public getStoredTokenData(): StoredTokenData | null {
    if (!this.token || !this.expirationTime || !this.currentFacilityId) {
      return null;
    }
    if (!this.userInfo || !this.facilityInfo || !this.roleInfo) {
      return null;
    }
    return {
      token: this.token,
      expirationTime: this.expirationTime,
      facilityId: this.currentFacilityId,
      user: this.userInfo,
      facility: this.facilityInfo,
      role: this.roleInfo,
    };
  }

  public isAdmin(): boolean {
    return this.userInfo?.IsAdmin ?? false;
  }

  public getCurrentFacilityId(): string | null {
    return this.currentFacilityId;
  }
}

// ============================================================================
// Exported Helper Functions
// ============================================================================

function extractErrorMessage(
  errorData: Record<string, unknown>,
  fallback: string,
): string {
  if (Array.isArray(errorData.detail)) {
    const messages = errorData.detail
      .map((err: { msg?: string; loc?: (string | number)[] }) => {
        if (typeof err.msg === "string") {
          if (Array.isArray(err.loc) && err.loc.length > 0) {
            const field = err.loc[err.loc.length - 1];
            return `${field}: ${err.msg}`;
          }
          return err.msg;
        }
        return null;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join("; ") : fallback;
  }

  if (typeof errorData.detail === "string") {
    return errorData.detail;
  }

  if (typeof errorData.message === "string") {
    return errorData.message;
  }

  return fallback;
}

async function getBackendToken(facilityId: string): Promise<string> {
  const tokenManager = MemberManagementTokenManager.getInstance();
  const token = await tokenManager.fetchToken(facilityId);
  if (!token) {
    throw new Error("Unauthorized");
  }
  return token;
}

// ============================================================================
// Member CRUD Operations
// ============================================================================

export async function inviteMember(
  facilityId: string,
  payload: InviteMemberPayload,
): Promise<InviteMemberResponse> {
  const token = await getBackendToken(facilityId);
  const endpoints = getApiEndpoints();

  const response = await fetch(endpoints.MEMBER_INVITE, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

export async function removeMember(
  facilityId: string,
  userId: string,
): Promise<RemoveMemberResponse> {
  const token = await getBackendToken(facilityId);
  const endpoints = getApiEndpoints();

  const response = await fetch(`${endpoints.MEMBER_BASE}/${userId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

export async function updateMemberRole(
  facilityId: string,
  userId: string,
  payload: UpdateMemberRolePayload,
): Promise<UpdateMemberRoleResponse> {
  const token = await getBackendToken(facilityId);
  const endpoints = getApiEndpoints();

  const response = await fetch(`${endpoints.MEMBER_BASE}/${userId}/role`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

/**
 * Reset a member's TOTP second factor (super admin only).
 *
 * Forwards to backend-service which clears the member's enrolled authenticator
 * and active MFA sessions, forcing re-enrollment on their next sign-in.
 *
 * @param facilityId - Facility id used to mint the RBAC bearer token
 * @param userId - Provider UID whose 2FA should be reset
 * @returns Reset confirmation from backend-service
 */
export async function resetMemberTwoFactor(
  facilityId: string,
  userId: string,
): Promise<ResetTwoFactorResponse> {
  const token = await getBackendToken(facilityId);
  const endpoints = getApiEndpoints();

  const response = await fetch(endpoints.MEMBER_RESET_2FA(userId), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

/**
 * Permanently delete a member from every facility and from auth (super admin only).
 *
 * Forwards to backend-service, which strips the member from all facilities,
 * removes pending invitations, asks auth-service to delete the Firebase Auth
 * account and clear MFA state, and finally removes the provider document
 * (last, so a failed run can be retried). After this call the member can no
 * longer sign in or hold any access.
 *
 * @param facilityId - Facility id used to mint the RBAC bearer token
 * @param userId - Provider UID to permanently delete
 * @returns Deletion confirmation from backend-service
 */
export async function deleteMember(
  facilityId: string,
  userId: string,
): Promise<DeleteMemberResponse> {
  const token = await getBackendToken(facilityId);
  const endpoints = getApiEndpoints();

  const response = await fetch(endpoints.MEMBER_DELETE(userId), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

export async function bulkUpdateMember(
  facilityId: string,
  payload: BulkUpdatePayload,
): Promise<BulkUpdateResponse> {
  const token = await getBackendToken(facilityId);
  const endpoints = getApiEndpoints();

  const response = await fetch(endpoints.MEMBER_BULK_UPDATE, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: MemberManagementError = {
      status: response.status,
      message: extractErrorMessage(errorData, response.statusText),
    };
    throw error;
  }

  return response.json();
}

// ============================================================================
// RBAC Token Functions
// ============================================================================

export function clearMemberManagementToken(): void {
  MemberManagementTokenManager.getInstance().clearToken();
}

/**
 * Subscribe to RBAC role snapshot changes for `useSyncExternalStore`.
 *
 * @param onStoreChange - Called when cached role id may have changed.
 * @returns Unsubscribe callback.
 */
export function subscribeToRbacRoleStore(
  onStoreChange: () => void,
): () => void {
  return MemberManagementTokenManager.getInstance().subscribeToRbacRole(
    onStoreChange,
  );
}

/**
 * Current cached RBAC role id for `useSyncExternalStore` snapshots.
 *
 * @returns `role.id` from the last successful token exchange, or `""`.
 */
export function getRbacRoleIdSnapshot(): string {
  return MemberManagementTokenManager.getInstance().getRbacRoleIdSnapshot();
}

export async function fetchRbacToken(
  facilityId: string,
): Promise<RbacTokenResult> {
  const tokenManager = MemberManagementTokenManager.getInstance();
  const token = await tokenManager.fetchToken(facilityId);

  if (token !== null) {
    return { success: true };
  }

  return {
    success: false,
    errorType: tokenManager.getLastErrorType() ?? "unknown",
    errorMessage:
      tokenManager.getLastErrorMessage() ??
      "An error occurred. Please try again.",
  };
}

/**
 * Submit a TOTP code to complete a pending second-factor challenge.
 *
 * @param code - 6-digit code from the user's authenticator app
 * @returns RBAC token result describing success or the failure reason
 */
export async function verifyTotpCode(code: string): Promise<RbacTokenResult> {
  return MemberManagementTokenManager.getInstance().verifyTotp(code);
}

/**
 * Subscribe to MFA challenge state changes for `useSyncExternalStore`.
 *
 * @param onStoreChange - Called when the MFA challenge state changes.
 * @returns Unsubscribe callback.
 */
export function subscribeToMfaState(onStoreChange: () => void): () => void {
  return MemberManagementTokenManager.getInstance().subscribeToMfaState(
    onStoreChange,
  );
}

/**
 * Current pending MFA challenge snapshot for `useSyncExternalStore`.
 *
 * @returns The MFA challenge state (status null when no challenge is pending).
 */
export function getMfaStateSnapshot(): MfaChallengeState {
  return MemberManagementTokenManager.getInstance().getMfaStateSnapshot();
}

export function getTokenUserInfo(): TokenUserInfo | null {
  return MemberManagementTokenManager.getInstance().getUserInfo();
}

export function getTokenFacilityInfo(): TokenFacilityInfo | null {
  return MemberManagementTokenManager.getInstance().getFacilityInfo();
}

/**
 * Get facility IDs available when the selected facility token was rejected.
 *
 * @returns Facility IDs the authenticated user may select for recovery
 */
export function getFacilityRecoveryIds(): string[] {
  return MemberManagementTokenManager.getInstance().getRecoveryFacilityIds();
}

export function getTokenRoleInfo(): TokenRoleInfo | null {
  return MemberManagementTokenManager.getInstance().getRoleInfo();
}

export function getTokenPermissions(): TokenPermissions | null {
  return MemberManagementTokenManager.getInstance().getPermissions();
}

export function hasTokenPermission(
  permission: keyof TokenPermissions,
): boolean {
  return MemberManagementTokenManager.getInstance().hasPermission(permission);
}

export function getStoredTokenData(): StoredTokenData | null {
  return MemberManagementTokenManager.getInstance().getStoredTokenData();
}

const ADMIN_ROLE_IDS: ReadonlySet<string> = new Set<string>([
  RbacRoleIdEnum.ADMIN_MED_ONC,
  RbacRoleIdEnum.ADMIN_PHARMA,
  RbacRoleIdEnum.SUPER_ADMIN,
]);

const STANDARD_ROLE_IDS: ReadonlySet<string> = new Set<string>([
  RbacRoleIdEnum.STANDARD_MED_ONC,
  RbacRoleIdEnum.STANDARD_PHARMA,
]);

export function isCurrentUserAdmin(): boolean {
  const roleInfo = MemberManagementTokenManager.getInstance().getRoleInfo();
  return roleInfo?.id ? ADMIN_ROLE_IDS.has(roleInfo.id) : false;
}

export function getCurrentFacilityId(): string | null {
  return MemberManagementTokenManager.getInstance().getCurrentFacilityId();
}

export function getUserInternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getUserInfo()
      ?.internal_healthcare_facilities ?? []
  );
}

export function getUserExternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getUserInfo()
      ?.external_healthcare_facilities ?? []
  );
}

export function getFacilityInternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getFacilityInfo()
      ?.internal_facilities ?? []
  );
}

export function getFacilityExternalFacilities(): string[] {
  return (
    MemberManagementTokenManager.getInstance().getFacilityInfo()
      ?.external_facilities ?? []
  );
}

export function mapUiRoleToApiRoleId(
  uiRole: "admin" | "member" | "Admin" | "Member",
):
  | typeof RbacRoleIdEnum.ADMIN_MED_ONC
  | typeof RbacRoleIdEnum.STANDARD_MED_ONC {
  const lowerRole = uiRole.toLowerCase();
  return lowerRole === "admin"
    ? RbacRoleIdEnum.ADMIN_MED_ONC
    : RbacRoleIdEnum.STANDARD_MED_ONC;
}

export function mapApiRoleIdToUiRole(roleId: string): "Admin" | "Member" {
  return ADMIN_ROLE_IDS.has(roleId) ? "Admin" : "Member";
}

export function getMemberErrorMessage(error: MemberManagementError): string {
  switch (error.status) {
    case 401:
      return "You are not authenticated. Please log in again.";
    case 403:
      return "You do not have permission to perform this action. Admin access required.";
    case 404:
      return "Member not found in this facility.";
    case 409:
      return "This member already exists in the facility or has a pending invitation.";
    case 422:
      return error.message || "Validation error. Please check your input.";
    case 500:
      return "An unexpected server error occurred. Please try again later.";
    default:
      return error.message || "An error occurred. Please try again.";
  }
}

export function isCurrentUserSuperAdmin(): boolean {
  const roleInfo = MemberManagementTokenManager.getInstance().getRoleInfo();
  return roleInfo?.id === RbacRoleIdEnum.SUPER_ADMIN;
}

// ============================================================================
// Super Admin Helper Functions
// ============================================================================

interface ProviderDocument {
  uid?: string;
  FirstName?: string;
  LastName?: string;
  EmailAddresses?: string[];
  Status?: string;
  internal_healthcare_facilities?: string[];
  external_healthcare_facilities?: string[];
  healthcare_facilities?: string[];
}

interface FacilityProviderDocument {
  role_id?: string;
  Status?: string;
}

export async function fetchAllFacilities(): Promise<FacilityOption[]> {
  try {
    const facilities =
      await FirestoreService.getAllDocuments<HealthCareFacilityModel>(
        FirestoreCollectionReference.healthcareFacility(),
      );

    return facilities.map((facility) => ({
      id:
        (facility as HealthCareFacilityModel & { id?: string }).id ||
        facility.DocId ||
        "",
      name: facility.Name || "Unknown Facility",
      isExternal: facility.is_external_organization === true,
    }));
  } catch (error) {
    logError(error as Error, "Error fetching all facilities");
    throw error;
  }
}

export async function fetchAllProviders(
  facilityMap: Map<string, FacilityOption>,
): Promise<ProviderWithFacilities[]> {
  try {
    const providers = await FirestoreService.getAllDocuments<ProviderDocument>(
      FirestoreCollectionReference.providers(),
    );

    const providersWithFacilities: ProviderWithFacilities[] = [];

    for (const provider of providers) {
      if (!provider.uid || provider.Status === "disabled") continue;

      const internalFacilityIds = provider.internal_healthcare_facilities || [];
      const externalFacilityIds = provider.external_healthcare_facilities || [];

      const internalFacilityNames = internalFacilityIds
        .map((id) => facilityMap.get(id)?.name || id)
        .filter(Boolean);
      const externalFacilityNames = externalFacilityIds
        .map((id) => facilityMap.get(id)?.name || id)
        .filter(Boolean);

      const facilityRoles: Record<string, AssignableRoleId> = {};

      const allFacilityIds = [...internalFacilityIds, ...externalFacilityIds];
      for (const facilityId of allFacilityIds) {
        try {
          const providerDoc =
            await FirestoreService.getDocument<FacilityProviderDocument>(
              `${FirestoreCollectionReference.healthFacilityProviders(facilityId)}/${provider.uid}`,
            );
          if (providerDoc?.role_id) {
            facilityRoles[facilityId] = providerDoc.role_id as AssignableRoleId;
          }
        } catch {
          facilityRoles[facilityId] = RbacRoleIdEnum.STANDARD_MED_ONC;
        }
      }

      providersWithFacilities.push({
        id: provider.uid,
        name:
          `${provider.FirstName || ""} ${provider.LastName || ""}`.trim() ||
          "Unknown",
        email: provider.EmailAddresses?.[0] || "",
        status: provider.Status || "active",
        internalFacilityIds,
        externalFacilityIds,
        internalFacilityNames,
        externalFacilityNames,
        facilityRoles,
      });
    }

    return providersWithFacilities;
  } catch (error) {
    logError(error as Error, "Error fetching all providers");
    throw error;
  }
}

export async function fetchSuperAdminData(): Promise<{
  facilities: FacilityOption[];
  internalFacilities: FacilityOption[];
  externalFacilities: FacilityOption[];
  providers: ProviderWithFacilities[];
}> {
  try {
    const facilities = await fetchAllFacilities();

    const facilityMap = new Map<string, FacilityOption>();
    facilities.forEach((f) => facilityMap.set(f.id, f));

    const internalFacilities = facilities.filter((f) => !f.isExternal);
    const externalFacilities = facilities.filter((f) => f.isExternal);

    const providers = await fetchAllProviders(facilityMap);

    return {
      facilities,
      internalFacilities,
      externalFacilities,
      providers,
    };
  } catch (error) {
    logError(error as Error, "Error fetching super admin data");
    throw error;
  }
}

export const userLoggedInFacilityType = (orgId: string): FacilityType => {
  const userInfo = getTokenUserInfo();

  if (!userInfo || !orgId) {
    return "not_assigned";
  }

  const internalFacilities = userInfo.internal_healthcare_facilities ?? [];
  const externalFacilities = userInfo.external_healthcare_facilities ?? [];

  if (internalFacilities.includes(orgId)) {
    return "internal";
  }

  if (externalFacilities.includes(orgId)) {
    return "external";
  }

  return "not_assigned";
};

export function getCurrentUserId(): string | undefined {
  return MemberManagementTokenManager.getInstance().getUserInfo()?.id;
}

/**
 * Return the current RBAC access token (the unified-auth user JWT) for the
 * logged-in facility, refreshing it transparently when near expiry.
 *
 * Under unified-auth this is the only token backend services accept on
 * post-login API calls; dashboards must send it as
 * ``Authorization: Bearer <token>``. It supersedes the legacy
 * ``risa_front_end_user`` bearer credential, which the new auth-service no
 * longer recognises.
 *
 * @return Promise resolving to the RBAC JWT, or ``null`` before login / when
 *   no facility is selected.
 */
export async function getRbacAccessToken(): Promise<string | null> {
  const tokenManager = MemberManagementTokenManager.getInstance();
  const facilityId = tokenManager.getCurrentFacilityId();
  if (!facilityId) {
    return null;
  }
  return tokenManager.fetchToken(facilityId);
}
